import { tryCatch } from "@/lib/try-catch";
import { Ok, Err } from "ts-results";
import { API_BASE_URL, EQUIPMENT_VALUES } from "@/lib/constants";
import { Response } from "./types";
import { z } from "zod";

export const createEquipmentFormSchema = z.object({
  typeId: z.enum(
    EQUIPMENT_VALUES.map((value) => value.value) as [string, ...string[]]
  ),
  serialNumber: z.string().min(1, "El número de serie es requerido"),
  brand: z.string().optional(),
  model: z.string().optional(),
  calibratedAt: z.string().optional(),
  nextCalibration: z.string().optional(),
  certificate: z.instanceof(File).optional(),
  enableCalibrationCurve: z.boolean().default(false),
  calibrationIntervals: z
    .array(
      z.object({
        start: z.number().optional(),
        end: z.number().optional(),
        offset: z.number().optional(),
        scalar: z.number().optional(),
      })
    )
    .optional(),
});

export type EquipmentCreateRequest = z.infer<typeof createEquipmentFormSchema>;

export async function equipmentCreate(
  request: EquipmentCreateRequest
): Promise<Response<string>> {
  const formData = new FormData();
  formData.append("typeId", request.typeId);
  formData.append("serialNumber", request.serialNumber);
  formData.append("brand", request.brand ?? "");
  formData.append("model", request.model ?? "");
  if (request.calibratedAt) {
    formData.append("calibratedAt", request.calibratedAt);
  }
  if (request.nextCalibration) {
    formData.append("nextCalibration", request.nextCalibration);
  }
  if (request.certificate) {
    formData.append("certificate", request.certificate);
  }
  if (
    request.enableCalibrationCurve &&
    request.calibrationIntervals &&
    request.calibrationIntervals.length > 0
  ) {
    formData.append(
      "correctionIntervals",
      JSON.stringify(request.calibrationIntervals)
    );
  }
  const responseResult = await tryCatch(
    fetch(`${API_BASE_URL}/api/v1/equipos`, {
      method: "POST",
      credentials: "include",
      body: formData,
    })
  );

  if (responseResult.err) {
    return Err({
      message: `Error al consultar el servidor: ${responseResult.val.message}`,
      devMessage: responseResult.val.message,
    });
  }

  const response = responseResult.val;

  if (!response.ok) {
    const errorMessageResult = await tryCatch(response.json());
    return errorMessageResult.err
      ? Err({
          message: `Error al agregar el equipo: ${errorMessageResult.val.message}`,
          devMessage: errorMessageResult.val.message,
        })
      : Err({
          message: errorMessageResult.val.message,
          devMessage: errorMessageResult.val.devMessage,
        });
  }

  const dataResult = await tryCatch(response.text());

  if (dataResult.err) {
    return Err({
      message: `Error al leer la respuesta del servidor: ${dataResult.val.message}`,
      devMessage: dataResult.val.message,
    });
  }

  return Ok(dataResult.val);
}
